import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faClipboardCheck,
  faClock,
  faChartColumn,
  faFilter,
  faForward,
  faListCheck,
  faLock,
  faPlus,
  faEye,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
  faUserCheck,
  faBolt,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import { getCurrentUserIdentity, isCustomerIdentity } from "../Tickets/ticketUtils";
import ForwardTaskModal from "./TasksModels/ForwardTaskModal";
import TaskComposerModal from "./TasksModels/TaskComposerModal";
import TaskHistoryModal from "./TasksModels/TaskHistoryModal";
import {
  createTask,
  fetchEmployees,
  forwardTask,
  getAllTaskListPaged,
  getMyTaskListPaged,
  updateTask,
  updateTaskProgress,
  updateTaskStatus,
} from "./taskService";
import {
  formatTaskDate,
  getTaskCurrentOwner,
  getTaskForwardCount,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  isTaskCompleted,
  isTaskForwardLocked,
  normalizeTaskProgressPercent,
  TASK_STATUS_OPTIONS,
} from "./taskUtils";

const getPageCopy = (isMyTaskMode, requestedUserId, currentUserId) => {
  const isViewingAnotherUser = Boolean(requestedUserId) && Boolean(currentUserId) && String(requestedUserId) !== String(currentUserId);

  if (isMyTaskMode) {
    return {
      breadcrumb: "My Task",
      title: "My Task Desk",
      subtitle: "Your personal workspace",
      summary: isViewingAnotherUser
        ? "Showing tasks created by or currently assigned to the selected user"
        : "Showing tasks you created or tasks currently assigned to you",
      error: "My task workspace could not be loaded.",
      empty: isViewingAnotherUser
        ? "No created or received task was found for this user."
        : "No created or received task is available for your login right now.",
    };
  }

  return {
    breadcrumb: "All Task",
    title: "All Task Desk",
    subtitle: "Team-wide task overview",
    summary: "Showing all task records",
    error: "All task workspace could not be loaded.",
    empty: "No task record was found yet.",
  };
};

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
const DEFAULT_PAGE_SIZE = 10;
const MAX_VISIBLE_PAGES = 9;

export default function TasksPage({ mode = "all" }) {
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const isCustomer = useMemo(() => isCustomerIdentity(identity), [identity]);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isMyTaskMode = mode === "mine";
  const requestedUserId = searchParams.get("userId") || identity.userId || "";
  const pageCopy = useMemo(
    () => getPageCopy(isMyTaskMode, requestedUserId, identity.userId),
    [identity.userId, isMyTaskMode, requestedUserId]
  );

  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageMeta, setPageMeta] = useState({
    totalCount: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    waiting: 0,
    completed: 0,
  });
  const [composerContext, setComposerContext] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [forwardingTask, setForwardingTask] = useState(null);
  const [historyTask, setHistoryTask] = useState(null);
  const [forwardAssigneeId, setForwardAssigneeId] = useState("");
  const [forwardNote, setForwardNote] = useState("");
  const [progressDrafts, setProgressDrafts] = useState({});

  const loadPageData = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [taskResult, employeeItems] = await Promise.all([
        isMyTaskMode
          ? getMyTaskListPaged({
            userId: requestedUserId,
            pageNumber,
            pageSize,
            searchText,
            status: statusFilter,
            assignedEmployeeId: assigneeFilter === "All" ? 0 : Number(assigneeFilter),
          })
          : getAllTaskListPaged({
            pageNumber,
            pageSize,
            searchText,
            status: statusFilter,
            assignedEmployeeId: assigneeFilter === "All" ? 0 : Number(assigneeFilter),
          }),
        fetchEmployees(),
      ]);

      setTasks(taskResult.items || []);
      setPageMeta({
        totalCount: taskResult.totalCount || 0,
        totalPages: taskResult.totalPages || 0,
        pageNumber: taskResult.pageNumber || 1,
        pageSize: taskResult.pageSize || DEFAULT_PAGE_SIZE,
        hasNextPage: Boolean(taskResult.hasNextPage),
        hasPreviousPage: Boolean(taskResult.hasPreviousPage),
      });
      setSummary(taskResult.summary || {});
      setEmployees(employeeItems);
    } catch (loadError) {
      setTasks([]);
      setPageMeta((current) => ({
        ...current,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      }));
      setSummary({ total: 0, pending: 0, inProgress: 0, waiting: 0, completed: 0 });
      setError(loadError?.message || pageCopy.error);
    } finally {
      setLoading(false);
    }
  }, [assigneeFilter, isMyTaskMode, pageCopy.error, pageNumber, pageSize, requestedUserId, searchText, statusFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchInput.trim());
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => { setPageNumber(1); }, [statusFilter, assigneeFilter]);
  useEffect(() => { loadPageData(); }, [loadPageData]);

  useEffect(() => {
    setProgressDrafts(
      tasks.reduce((result, task) => {
        result[task.id] = normalizeTaskProgressPercent(task.progressPercent);
        return result;
      }, {})
    );
  }, [tasks]);

  const counts = useMemo(() => ({
    total: summary.total || 0,
    pending: summary.pending || 0,
    progress: summary.inProgress || 0,
    waiting: summary.waiting || 0,
    completed: summary.completed || 0,
  }), [summary]);

  const paginationNumbers = useMemo(
    () => buildVisiblePageNumbers(pageMeta.pageNumber, pageMeta.totalPages, MAX_VISIBLE_PAGES),
    [pageMeta.pageNumber, pageMeta.totalPages]
  );

  const rangeLabel = useMemo(() => {
    if (!pageMeta.totalCount || !tasks.length) return "Showing 0 tasks";
    const start = (pageMeta.pageNumber - 1) * pageMeta.pageSize + 1;
    const end = start + tasks.length - 1;
    return `Showing ${start}–${end} of ${pageMeta.totalCount}`;
  }, [pageMeta.pageNumber, pageMeta.pageSize, pageMeta.totalCount, tasks.length]);

  const handleCreateTask = async (values, sourceTicket = null) => {
    setSaving(true);
    setError("");
    try {
      await createTask(
        {
          ...values,
          ticketId: Number(sourceTicket?.id || sourceTicket?.ticketId || 0),
          ticketNumber: sourceTicket?.ticketNumber || "",
          ticketSubject: sourceTicket?.subject || sourceTicket?.ticketSubject || "",
          ticketStatus: sourceTicket?.status || sourceTicket?.ticketStatus || "",
          sourceType: sourceTicket ? "SupportTicket" : "ManualTask",
          createdByUserId: identity.userId,
          createdByName: identity.fullName || identity.email || "User",
        },
        identity
      );
      setComposerContext(null);
      await loadPageData();
    } catch (createError) {
      setError(createError?.message || "Task could not be created.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTask = async (values) => {
    if (!editingTask) return;
    if (isTaskCompleted(editingTask)) {
      setError("Completed tasks are locked and cannot be edited.");
      setEditingTask(null);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateTask(editingTask.id, { ...editingTask, ...values }, identity, "Task Updated", "Task details were updated");
      setEditingTask(null);
      await loadPageData();
    } catch (updateError) {
      setError(updateError?.message || "Task could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (task, nextStatus) => {
    if (nextStatus === task.status) return;
    if (isTaskCompleted(task)) { setError("Completed tasks are locked and cannot change status."); return; }
    if (!window.confirm(`Change task status to "${nextStatus}"?`)) return;
    setSaving(true);
    setError("");
    try {
      await updateTaskStatus(task, nextStatus, identity);
      await loadPageData();
    } catch (updateError) {
      setError(updateError?.message || "Task status could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  const handleProgressSave = async (task) => {
    if (isTaskCompleted(task)) { setError("Completed tasks are locked and cannot change progress."); return; }
    const nextProgress = normalizeTaskProgressPercent(progressDrafts[task.id]);
    if (nextProgress === normalizeTaskProgressPercent(task.progressPercent)) return;
    if (!window.confirm(`Update task progress to ${nextProgress}%?`)) return;
    setSaving(true);
    setError("");
    try {
      await updateTaskProgress(task, nextProgress, identity);
      await loadPageData();
    } catch (updateError) {
      setError(updateError?.message || "Task progress could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  const handleForwardTask = async () => {
    if (!forwardingTask) return;
    if (isTaskCompleted(forwardingTask)) {
      setError("Completed tasks are locked and cannot be forwarded.");
      setForwardingTask(null); setForwardAssigneeId(""); setForwardNote("");
      return;
    }
    if (isTaskForwardLocked(forwardingTask)) { setError("Forward is locked because the current timeline item is already forwarded."); return; }
    const assignee = employees.find((item) => String(item.id) === String(forwardAssigneeId || ""));
    if (!assignee) { setError("Select an employee to forward this task."); return; }
    setSaving(true);
    setError("");
    try {
      await forwardTask(forwardingTask, assignee, identity, forwardNote);
      setForwardingTask(null); setForwardAssigneeId(""); setForwardNote("");
      await loadPageData();
    } catch (forwardError) {
      setError(forwardError?.message || "Task could not be forwarded.");
    } finally {
      setSaving(false);
    }
  };

  if (isCustomer) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0F111A] p-4 md:p-6">
        <Breadcrumb pageName={pageCopy.breadcrumb} currentPage={pageCopy.breadcrumb} />
        <div className="mt-6 rounded-3xl border border-orange-100 dark:border-gray-800 bg-white dark:bg-[#161B22] px-6 py-16 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-lg"
            style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}>
            <FontAwesomeIcon icon={faListCheck} />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Task management is for staff users</h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">Customers can continue using support tickets, while employees manage follow-up work here.</p>
          <Link
            to="/tickets"
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
            style={{ background: "linear-gradient(to right, #C04C2A, #4A1B13)" }}
          >
            <FontAwesomeIcon icon={faTicket} />
            Open Tickets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0F111A] p-4 md:p-6">
      <Breadcrumb pageName={pageCopy.breadcrumb} currentPage={pageCopy.breadcrumb} />

      <div className="mt-4 overflow-hidden rounded-3xl border border-orange-100 dark:border-gray-800 bg-white dark:bg-[#161B22] shadow-xl">

        {/* ── Stats + Actions Hero ── */}
        <div
          className="relative overflow-hidden px-5 pt-6 pb-5 md:px-7 md:pt-7 md:pb-6"
          style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}
        >
          {/* decorative blobs */}
          <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/4 h-28 w-28 rounded-full blur-3xl" style={{ background: "rgba(255,160,100,0.12)" }} />

          {/* Top row: title left, action buttons right */}
          <div className="relative mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/15">
                  <FontAwesomeIcon icon={faClipboardCheck} className="text-sm text-white" />
                </div>
                <h1 className="text-lg font-extrabold tracking-tight text-white">{pageCopy.title}</h1>
              </div>
              <p className="mt-1 pl-10 text-[11px] text-orange-200">{identity.fullName || "User"} &nbsp;·&nbsp; {pageCopy.summary}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setComposerContext({ ticket: null })}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-bold shadow-md transition hover:bg-orange-50 active:scale-95"
                style={{ color: "#C04C2A" }}
              >
                <FontAwesomeIcon icon={faPlus} />
                New Task
              </button>
              <Link
                to={requestedUserId ? `/task-history?userId=${encodeURIComponent(requestedUserId)}` : "/task-history"}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={faClock} />
                History
              </Link>

              <Link
                to={requestedUserId ? `/task-observers?userId=${encodeURIComponent(requestedUserId)}` : "/task-observers"}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={faEye} />
                Observer
              </Link>
              <Link
                to={isMyTaskMode ? (requestedUserId ? `/task-report?userId=${encodeURIComponent(requestedUserId)}` : "/task-report") : "/all-task-report"}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={faChartColumn} />
                Report
              </Link>
              <button
                type="button"
                onClick={loadPageData}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>

          {/* Stat cards inside the hero */}
          <div className="relative grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <HeroStatCard label="Total" value={counts.total} icon={faListCheck} cardBg="rgba(255,255,255,0.13)" iconBg="rgba(255,255,255,0.22)" onClick={() => setStatusFilter("All")} isActive={statusFilter === "All"} activeColor="#fff" />
            <HeroStatCard label="Pending" value={counts.pending} icon={faClipboardCheck} cardBg="rgba(253,211,77,0.18)" iconBg="rgba(253,211,77,0.35)" onClick={() => setStatusFilter("Pending")} isActive={statusFilter === "Pending"} activeColor="#fde68a" />
            <HeroStatCard label="In Progress" value={counts.progress} icon={faBolt} cardBg="rgba(96,165,250,0.18)" iconBg="rgba(96,165,250,0.35)" onClick={() => setStatusFilter("In Progress")} isActive={statusFilter === "In Progress"} activeColor="#93c5fd" />
            <HeroStatCard label="Waiting" value={counts.waiting} icon={faFilter} cardBg="rgba(251,146,60,0.18)" iconBg="rgba(251,146,60,0.35)" onClick={() => setStatusFilter("Waiting")} isActive={statusFilter === "Waiting"} activeColor="#fed7aa" />
            <HeroStatCard label="Completed" value={counts.completed} icon={faCheckCircle} cardBg="rgba(52,211,153,0.18)" iconBg="rgba(52,211,153,0.35)" onClick={() => setStatusFilter("Completed")} isActive={statusFilter === "Completed"} activeColor="#6ee7b7" />
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="p-5 md:p-6">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="relative min-w-[220px]">
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C04C2A]/60" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search task, ticket, or assignee"
                  className="w-full rounded-2xl border border-orange-100 dark:border-gray-700 bg-orange-50/40 dark:bg-gray-800 py-2.5 pl-11 pr-4 text-sm text-slate-700 dark:text-white outline-none transition focus:ring-2 focus:bg-white dark:focus:bg-gray-800"
                  style={{ "--tw-ring-color": "rgba(192,76,42,0.15)" }}
                  onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }}
                  onBlur={(e) => { e.target.style.borderColor = ""; }}
                />
              </div>
              {[
                <select key="status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-2xl border border-orange-100 dark:border-gray-700 bg-orange-50/40 dark:bg-gray-800 px-4 py-2.5 text-sm text-slate-700 dark:text-white outline-none transition"
                  onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }} onBlur={(e) => { e.target.style.borderColor = ""; }}>
                  <option value="All">All Status</option>
                  {TASK_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>,
                <select key="assignee" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="rounded-2xl border border-orange-100 dark:border-gray-700 bg-orange-50/40 dark:bg-gray-800 px-4 py-2.5 text-sm text-slate-700 dark:text-white outline-none transition"
                  onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }} onBlur={(e) => { e.target.style.borderColor = ""; }}>
                  <option value="All">All Assignees</option>
                  {employees.map((emp) => <option key={emp.id || emp.userId} value={emp.id}>{emp.fullName}{emp.workEmail ? ` - ${emp.workEmail}` : ""}</option>)}
                </select>,
                <select key="pagesize" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE); setPageNumber(1); }}
                  className="rounded-2xl border border-orange-100 dark:border-gray-700 bg-orange-50/40 dark:bg-gray-800 px-4 py-2.5 text-sm text-slate-700 dark:text-white outline-none transition"
                  onFocus={(e) => { e.target.style.borderColor = "#C04C2A"; }} onBlur={(e) => { e.target.style.borderColor = ""; }}>
                  {PAGE_SIZE_OPTIONS.map((o) => <option key={o} value={o}>Page Size: {o}</option>)}
                </select>,
              ]}
            </div>
            <div className="text-sm font-medium text-slate-400 dark:text-gray-400">{rangeLabel}</div>
          </div>

          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 dark:border-red-800 bg-rose-50 dark:bg-red-900/20 px-4 py-3 text-sm font-medium text-rose-700 dark:text-red-400">{error}</div>
          ) : null}

          {/* ── Table / States ── */}
          {loading ? (
            <div
              className="rounded-2xl border border-dashed border-orange-100 dark:border-gray-700 bg-orange-50/30 dark:bg-gray-800/30 px-6 py-16 text-center text-sm"
              style={{ color: "#C04C2A" }}
            >
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-lg" />
              Loading tasks...
            </div>
          ) : tasks.length ? (
            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-gray-800 shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm bg-white dark:bg-[#161B22]">
                  {/* ── Table Head ── */}
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-gray-800 text-left bg-gray-100 dark:bg-gray-800/50">
                      {["#", "Task", "Ticket", "Assignee", "Priority", "Status & Progress", "Due Date", "Actions"].map((col, i) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400"
                          style={i === 7 ? { textAlign: "right" } : {}}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {tasks.map((task, index) => {
                      const completedTask = isTaskCompleted(task);
                      const forwardLocked = isTaskForwardLocked(task);
                      const currentOwner = getTaskCurrentOwner(task);
                      const isActiveTaskPerson = String(currentOwner.assignedUserId || "") === String(identity.userId || "");
                      const isCreatedByMe = String(task.createdByUserId || "") === String(identity.userId || "");
                      const isReceivedTask = isMyTaskMode && !isCreatedByMe;
                      const progressDraftValue = normalizeTaskProgressPercent(progressDrafts[task.id] ?? task.progressPercent);
                      const pct = normalizeTaskProgressPercent(task.progressPercent);
                      const assigneeName = currentOwner.assignedEmployeeName || task.assignedEmployeeName || "?";
                      const initials = assigneeName.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

                      return (
                        <tr
                          key={task.id}
                          className={`group border-b dark:border-gray-800 transition-colors last:border-0 ${isReceivedTask
                            ? "bg-sky-50/20 dark:bg-blue-950/30"
                            : completedTask
                              ? "bg-emerald-50/20 dark:bg-emerald-950/30"
                              : "bg-white dark:bg-[#161B22]"
                            }`}
                        >
                          {/* # */}
                          <td className="w-10 px-4 py-3.5 align-middle">
                            <span className="flex h-6 w-6 items-center justify-center rounded-lg text-[10px] font-bold text-gray-500 dark:text-gray-400 bg-slate-100 dark:bg-gray-800">
                              {(pageMeta.pageNumber - 1) * pageMeta.pageSize + index + 1}
                            </span>
                          </td>

                          {/* Task */}
                          <td className="px-4 py-3.5 align-middle" style={{ minWidth: 220, maxWidth: 280 }}>
                            {/* left accent bar */}
                            <div className="flex items-start gap-2.5">
                              <div
                                className="mt-0.5 w-0.5 self-stretch rounded-full flex-shrink-0"
                                style={{ background: completedTask ? "#10b981" : isReceivedTask ? "#38bdf8" : "#C04C2A", minHeight: 36 }}
                              />
                              <div>
                                <div className="font-semibold leading-snug text-gray-800 dark:text-gray-200">{task.title}</div>
                                <div className="mt-0.5 line-clamp-1 text-[11px] text-gray-500 dark:text-gray-400">{task.description || "—"}</div>
                                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                                  {isMyTaskMode ? (
                                    <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${isReceivedTask ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>
                                      {isReceivedTask ? "Received" : "Created by me"}
                                    </span>
                                  ) : null}
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400">by {task.createdByName || "Unknown"}</span>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Ticket */}
                          <td className="px-4 py-3.5 align-middle" style={{ minWidth: 140 }}>
                            {task.ticketId ? (
                              <Link
                                to={`/tickets/${task.ticketId}?${new URLSearchParams({ backTo: `${location.pathname}${location.search}`, viewOnly: String(isReceivedTask) })}`}
                                state={{ backTo: `${location.pathname}${location.search}`, viewOnly: isReceivedTask }}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition hover:opacity-75"
                                style={{ borderColor: "rgba(192,76,42,0.25)", background: "rgba(192,76,42,0.06)", color: "#C04C2A" }}
                              >
                                <FontAwesomeIcon icon={faTicket} className="text-[10px]" />
                                {task.ticketNumber || "Ticket"}
                              </Link>
                            ) : (
                              <span className="rounded-lg px-2.5 py-1 text-[11px] text-gray-500 dark:text-gray-400 bg-slate-100 dark:bg-gray-800">Manual</span>
                            )}
                            {task.ticketSubject ? (
                              <div className="mt-1 line-clamp-1 max-w-[130px] text-[11px] text-gray-500 dark:text-gray-400">{task.ticketSubject}</div>
                            ) : null}
                          </td>

                          {/* Assignee */}
                          <td className="px-4 py-3.5 align-middle" style={{ minWidth: 160 }}>
                            <div className="flex items-center gap-2">
                              {/* avatar */}
                              <div
                                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white shadow-sm"
                                style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}
                              >
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-[12px] font-semibold text-gray-800 dark:text-gray-200">{assigneeName}</div>
                                <div className="truncate text-[10px] text-gray-500 dark:text-gray-400">{task.assignedEmployeeEmail || "—"}</div>
                              </div>
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1 pl-10">
                              {forwardLocked ? (
                                <span className="inline-flex items-center gap-0.5 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                                  <FontAwesomeIcon icon={faLock} className="text-[9px]" /> Locked
                                </span>
                              ) : null}
                              {completedTask ? (
                                <span className="inline-flex items-center gap-0.5 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                                  <FontAwesomeIcon icon={faLock} className="text-[9px]" /> Done
                                </span>
                              ) : null}
                              {getTaskForwardCount(task) > 0 ? (
                                <span className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: "rgba(192,76,42,0.08)", color: "#8B2A15" }}>
                                  {getTaskForwardCount(task)} fwd{getTaskForwardCount(task) > 1 ? "s" : ""}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          {/* Priority */}
                          <td className="px-4 py-3.5 align-middle">
                            <span className={`rounded-lg px-2.5 py-1 text-[11px] font-bold ${getTaskPriorityClasses(task.priority)}`}>
                              {task.priority}
                            </span>
                          </td>

                          {/* Status & Progress */}
                          <td className="px-4 py-3.5 align-middle" style={{ minWidth: 200 }}>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className={`rounded-lg px-2.5 py-0.5 text-[11px] font-bold ${getTaskStatusClasses(task.status)}`}>
                                  {task.status}
                                </span>
                              </div>
                              {/* progress bar */}
                              <div>
                                <div className="mb-1 flex items-center justify-between">
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Progress</span>
                                  <span className="text-[10px] font-bold" style={{ color: pct === 100 ? "#10b981" : "#C04C2A" }}>{pct}%</span>
                                </div>
                                <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                  <div
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{
                                      width: `${pct}%`,
                                      background: pct === 100
                                        ? "linear-gradient(to right,#34d399,#10b981)"
                                        : "linear-gradient(to right,#C04C2A,#4A1B13)",
                                    }}
                                  />
                                </div>
                              </div>
                              {/* status select + progress input */}
                              <div className="flex flex-wrap items-center gap-1.5">
                                <select
                                  value={task.status}
                                  onChange={(e) => handleStatusChange(task, e.target.value)}
                                  disabled={completedTask}
                                  className="rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-[11px] font-medium text-slate-600 dark:text-gray-200 outline-none transition disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-gray-900 disabled:text-slate-400 dark:disabled:text-gray-600"
                                >
                                  {TASK_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                                <input
                                  type="number" min="0" max="100"
                                  value={progressDraftValue}
                                  onChange={(e) => setProgressDrafts((cur) => ({ ...cur, [task.id]: normalizeTaskProgressPercent(e.target.value) }))}
                                  disabled={completedTask || !isActiveTaskPerson}
                                  className="w-14 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1 text-[11px] text-slate-600 dark:text-gray-200 outline-none disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-gray-900 disabled:text-slate-400 dark:disabled:text-gray-600"
                                />
                                <span className="text-[11px] text-gray-500 dark:text-gray-400">%</span>
                                <button
                                  type="button"
                                  onClick={() => handleProgressSave(task)}
                                  disabled={completedTask || !isActiveTaskPerson || saving || progressDraftValue === pct}
                                  className="rounded-lg px-2.5 py-1 text-[10px] font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-100 dark:disabled:bg-gray-900 disabled:text-slate-400 dark:disabled:text-gray-600"
                                  style={!(completedTask || !isActiveTaskPerson || saving || progressDraftValue === pct) ? { background: "linear-gradient(to right,#C04C2A,#4A1B13)" } : {}}
                                >
                                  Save
                                </button>
                              </div>
                              {completedTask ? (
                                <p className="text-[10px] text-gray-500 dark:text-gray-400">Completed — locked.</p>
                              ) : !isActiveTaskPerson ? (
                                <p className="text-[10px] text-gray-500 dark:text-gray-400">Only the active assignee can update.</p>
                              ) : (
                                <p className="text-[10px]" style={{ color: "#C04C2A" }}>100% auto-completes this task.</p>
                              )}
                            </div>
                          </td>

                          {/* Due Date */}
                          <td className="px-4 py-3.5 align-middle" style={{ minWidth: 120 }}>
                            <div className="text-[12px] font-semibold text-gray-800 dark:text-gray-200">{formatTaskDate(task.dueDate)}</div>
                            <div className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">Upd. {formatTaskDate(task.updatedAt, true)}</div>
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3.5 align-middle">
                            <div className="flex items-center justify-end gap-1.5">
                              <Link
                                to={`/tasks/${task.id}`}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 dark:text-gray-300 transition hover:border-slate-300 dark:hover:bg-gray-700 hover:bg-slate-50"
                              >
                                View
                              </Link>
                              <button
                                type="button"
                                onClick={() => setHistoryTask(task)}
                                className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition hover:opacity-80"
                                style={{ borderColor: "rgba(192,76,42,0.22)", background: "rgba(192,76,42,0.06)", color: "#C04C2A" }}
                              >
                                <FontAwesomeIcon icon={faClock} className="text-[9px]" />
                                Timeline
                              </button>
                              <button
                                type="button"
                                onClick={() => { if (!completedTask) setEditingTask(task); }}
                                disabled={completedTask}
                                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 dark:text-gray-300 transition hover:border-slate-300 dark:hover:bg-gray-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (completedTask || forwardLocked) return;
                                  setForwardingTask(task);
                                  setForwardAssigneeId(task.assignedEmployeeId ? String(task.assignedEmployeeId) : "");
                                  setForwardNote("");
                                }}
                                disabled={completedTask || forwardLocked}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:shadow-none disabled:bg-slate-100 dark:disabled:bg-gray-900 disabled:text-slate-400 dark:disabled:text-gray-600"
                                style={!(completedTask || forwardLocked) ? { background: "linear-gradient(to right,#C04C2A,#4A1B13)" } : {}}
                              >
                                <FontAwesomeIcon icon={completedTask || forwardLocked ? faLock : faForward} className="text-[9px]" />
                                {completedTask ? "Done" : forwardLocked ? "Locked" : "Forward"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-orange-200 dark:border-gray-700 bg-gradient-to-br from-orange-50/60 to-red-50/40 dark:bg-gray-800/30 px-6 py-16 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-lg"
                style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}>
                <FontAwesomeIcon icon={faListCheck} />
              </div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">No tasks found</h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{pageCopy.empty}</p>
              <button
                type="button"
                onClick={() => setComposerContext({ ticket: null })}
                className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
                style={{ background: "linear-gradient(to right, #C04C2A, #4A1B13)" }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Create Task
              </button>
            </div>
          )}

          {/* ── Pagination ── */}
          {tasks.length ? (
            <div className="mt-5 flex flex-col gap-4 bg-gray-50/50 dark:bg-gray-800/20 border-t dark:border-gray-800 pt-5 lg:flex-row lg:items-center lg:justify-between border-orange-100">
              <div className="text-sm text-gray-500 dark:text-gray-400">{rangeLabel}</div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPageNumber(1)}
                  disabled={!pageMeta.hasPreviousPage}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-orange-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:hover:bg-gray-700 hover:bg-orange-50 transition disabled:cursor-not-allowed disabled:opacity-40 dark:disabled:bg-gray-900 dark:disabled:text-gray-600"
                  style={{ color: "#C04C2A" }}
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </button>
                {paginationNumbers.map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setPageNumber(page)}
                    className={`inline-flex h-9 min-w-9 items-center justify-center rounded-full px-3 text-sm font-semibold transition ${page === pageMeta.pageNumber
                      ? ""
                      : "border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                      }`}
                    style={
                      page === pageMeta.pageNumber
                        ? { background: "linear-gradient(to right, #C04C2A, #4A1B13)", color: "#fff", borderColor: "#C04C2A", border: "1px solid #C04C2A" }
                        : {}
                    }
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPageNumber(pageMeta.totalPages || 1)}
                  disabled={!pageMeta.hasNextPage}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-orange-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:hover:bg-gray-700 hover:bg-orange-50 transition disabled:cursor-not-allowed disabled:opacity-40 dark:disabled:bg-gray-900 dark:disabled:text-gray-600"
                  style={{ color: "#C04C2A" }}
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <TaskComposerModal
        open={Boolean(composerContext)}
        ticket={composerContext?.ticket || null}
        employees={employees}
        identity={identity}
        submitting={saving}
        onClose={() => setComposerContext(null)}
        onSubmit={(values) => handleCreateTask(values, composerContext?.ticket || null)}
      />

      <TaskComposerModal
        open={Boolean(editingTask)}
        ticket={editingTask}
        employees={employees}
        identity={identity}
        initialValues={editingTask}
        submitting={saving}
        mode="edit"
        onClose={() => setEditingTask(null)}
        onSubmit={handleUpdateTask}
      />

      {forwardingTask ? (
        <ForwardTaskModal
          task={forwardingTask}
          employees={employees}
          saving={saving}
          assigneeId={forwardAssigneeId}
          note={forwardNote}
          onChangeAssignee={setForwardAssigneeId}
          onChangeNote={setForwardNote}
          onClose={() => { setForwardingTask(null); setForwardAssigneeId(""); setForwardNote(""); }}
          onSubmit={handleForwardTask}
        />
      ) : null}

      {historyTask ? <TaskHistoryModal task={historyTask} onClose={() => setHistoryTask(null)} /> : null}
    </div>
  );
}

/* ── Hero Stat Card ── */
function HeroStatCard({ label, value, icon, onClick, isActive, activeColor, cardBg, iconBg }) {
  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer overflow-hidden rounded-xl border border-white/15 px-3 py-2.5 backdrop-blur-sm transition-all duration-200"
      style={{
        background: isActive ? `${cardBg.replace("0.18)", "0.35)")}` : cardBg,
        borderColor: isActive ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.12)",
        boxShadow: isActive ? "0 2px 14px rgba(0,0,0,0.20)" : "none",
      }}
    >
      <div className="flex items-center justify-between gap-2">
        {/* icon */}
        <div
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-white transition-transform duration-200 group-hover:scale-110"
          style={{ background: isActive ? iconBg.replace("0.35)", "0.55)") : iconBg }}
        >
          <FontAwesomeIcon icon={icon} className="text-xs" />
        </div>
        {/* text */}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[10px] font-semibold uppercase tracking-wider" style={{ color: isActive ? activeColor : "rgba(255,230,210,0.75)" }}>
            {label}
          </div>
          <div className="text-xl font-extrabold leading-tight tracking-tight text-white">
            {value}
          </div>
        </div>
        {/* active pulse dot */}
        {isActive && (
          <span className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full" style={{ background: activeColor }} />
        )}
      </div>
      {/* bottom bar */}
      <div
        className="mt-2 h-px rounded-full transition-all duration-300"
        style={{ background: isActive ? activeColor : "rgba(255,255,255,0.12)", width: isActive ? "100%" : "35%", opacity: isActive ? 0.7 : 1 }}
      />
    </div>
  );
}

function buildVisiblePageNumbers(currentPage, totalPages, maxVisiblePages) {
  if (!totalPages) return [];
  const safeMax = Math.max(1, maxVisiblePages);
  let startPage = Math.max(1, currentPage - Math.floor(safeMax / 2));
  let endPage = startPage + safeMax - 1;
  if (endPage > totalPages) { endPage = totalPages; startPage = Math.max(1, endPage - safeMax + 1); }
  const pages = [];
  for (let page = startPage; page <= endPage; page += 1) pages.push(page);
  return pages;
}
